import { isValidUuid } from '@/util/is-valid-uuid'
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { getAuth } from 'firebase/auth'

import { AgentV2ConversationsActions, AgentV2RunPhase } from '@/agent-v2/conversations/store/slice'
import { selectAgentV2ConversationFromId, selectAgentV2RunPhase } from '@/agent-v2/conversations/store/selectors'
import { AgentV2MatterArtifactEditProposalActions } from '@/agent-v2/artifact-edit-review/store/slice'
import { reportDroppedAgentV2References, sanitizeAgentV2References } from '@/agent-v2/references/agent-v2-reference-sanitizer'
import { AgentV2ReferencesActions } from '@/agent-v2/references/store/slice'
import { UserSkillChatActions } from '@/user-skills/store/chat.slice'
import { saveAttachedSkillNames } from '@/user-skills/utils/attached-skill-storage'
import { resolveAttachedSkillDisplayNames } from '@/user-skills/utils/resolve-composer-submit-text'
import { AgentV2TranscriptActions } from '@/agent-v2/store/slice'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { selectUnavailableUserFileIds } from '@/user_files/files/store/selectors'
import { AgentV2ServerEventType, type AgentV2ServerEventEnvelope } from '@/agent-v2/store/schemas'
import {
  AgentV2SocketParseOutcome,
  buildAgentV2RunStopMessage,
  buildAgentV2TurnStartMessage,
  buildAgentV2WebSocketUrl,
  parseAgentV2SocketEvent,
} from '@/agent-v2/utils/agent-v2-contract'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import type { AppDispatch, RootState } from '@/store/store'
import { getIdTokenForWS2 } from '@/websocket_v2/ws2-util'
import { AgentV2TransportActions, AgentV2TransportPhase, type AgentV2StartTurnIntent } from './slice'

const kConnectionErrorMessage = 'Connection failed. Please try again.'

type TransportListenerApi = {
  dispatch: AppDispatch
  getState: () => RootState
}

type ActiveAgentV2Transport = {
  admittedTurnId: string | null
  clientRequestId: string
  conversationId: string
  effectSettled: Promise<void>
  lastAssistantDeltaSequence: number | null
  matterArtifactStatusById: Map<string, MatterArtifactVersionStatus>
  removeSocketListeners: (() => void) | null
  resolveEffect: () => void
  seenEventIds: Set<string>
  stopRequested: boolean
  terminalEventReceived: boolean
  turnStartSent: boolean
  websocket: WebSocket | null
}

// WebSockets are imperative, non-serializable resources. Redux owns only connection facts;
// this module-private registry owns each live one-turn connection across route lifetimes.
const activeTransports = new Map<string, ActiveAgentV2Transport>()

export const agentV2TransportListenerMiddleware = createListenerMiddleware()
const startListener = agentV2TransportListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

startListener({
  actionCreator: AgentV2TransportActions.startTurnRequested,
  effect: async (action, listenerApi) => {
    const intent = action.payload
    const originalTransportState = listenerApi.getOriginalState().agentV2TransportState.byConversationId[intent.conversationId]
    const transportState = listenerApi.getState().agentV2TransportState.byConversationId[intent.conversationId]

    // The reducer fences duplicate requests synchronously. Only the logical request accepted into
    // serializable state may create an imperative registry entry.
    if (originalTransportState && originalTransportState.phase !== AgentV2TransportPhase.IDLE) return
    if (transportState?.clientRequestId !== intent.clientRequestId) return
    const activeTransport = activeTransports.get(intent.conversationId)
    if (activeTransport?.clientRequestId === intent.clientRequestId) return
    if (!canStartTurn(intent, listenerApi.getState()) || activeTransport) {
      listenerApi.dispatch(AgentV2TransportActions.transportSettled({ conversationId: intent.conversationId, clientRequestId: intent.clientRequestId }))
      return
    }

    const entry = createActiveTransport(intent)
    activeTransports.set(intent.conversationId, entry)

    const library = listenerApi.getState().userSkillLibraryState
    const attachedSkillNames = resolveAttachedSkillDisplayNames(
      { selectedSkillIds: intent.selectedSkillIds, selectedPaxtonSkillNames: intent.selectedPaxtonSkillNames },
      library
    )
    const userId = getAuth().currentUser?.uid
    if (userId && attachedSkillNames.length) saveAttachedSkillNames(userId, intent.conversationId, intent.clientRequestId, attachedSkillNames)
    listenerApi.dispatch(
      AgentV2TranscriptActions.turnSubmitted({
        clientRequestId: intent.clientRequestId,
        conversationId: intent.conversationId,
        inputText: intent.inputText,
        attachedSkillNames,
        selectedSkillIds: intent.selectedSkillIds,
        selectedPaxtonSkillNames: intent.selectedPaxtonSkillNames,
        ...(intent.uiState?.table_id ? { tableId: intent.uiState.table_id } : {}),
        matterArtifactSelection: intent.matterArtifactSelection,
        attachedFiles: {
          fileIds: intent.searchDefaults.attached_file_ids,
          writingSampleFileId: intent.searchDefaults.writing_sample_file_id ?? null,
        },
        userMessageId: intent.userMessageId,
        assistantMessageId: intent.assistantMessageId,
      })
    )
    // The sent files become the chat's files and leave the composer, unless the turn replays an earlier one as it was
    if (!intent.replaysEarlierTurn) {
      const sentFiles = {
        conversationId: intent.conversationId,
        fileIds: intent.searchDefaults.attached_file_ids,
        writingSampleFileId: intent.searchDefaults.writing_sample_file_id ?? null,
      }
      listenerApi.dispatch(AgentV2ConversationsActions.setChatFiles(sentFiles))
      listenerApi.dispatch(AgentConversationFormActions.removeSentFiles(sentFiles))
    }
    listenerApi.dispatch(
      AgentConversationFormActions.removeDeletedFiles({
        conversationId: intent.conversationId,
        deletedFileIds: Object.keys(selectUnavailableUserFileIds(listenerApi.getState())),
      })
    )
    listenerApi.dispatch(
      AgentV2ConversationsActions.runSubmitted({
        clientRequestId: intent.clientRequestId,
        conversationId: intent.conversationId,
        request: {
          input_text: intent.inputText,
          matter_id: intent.matterId,
          search_defaults: intent.searchDefaults,
          ...(intent.selectedSkillIds?.length ? { selected_skill_ids: intent.selectedSkillIds } : {}),
          ...(intent.selectedPaxtonSkillNames?.length ? { selected_paxton_skill_names: intent.selectedPaxtonSkillNames } : {}),
          client_surface: listenerApi.getState().agentV2ConversationsState.hostSurface,
          ...(intent.matterArtifactSelection ? { matter_artifact_selection: intent.matterArtifactSelection } : {}),
          ...(intent.uiState ? { ui_state: intent.uiState } : {}),
        },
      })
    )

    let idToken: string
    try {
      idToken = await getIdTokenForWS2()
    } catch {
      failBeforeAdmission(entry, listenerApi)
      return
    }

    if (!isCurrentTransport(entry)) return

    let websocket: WebSocket
    try {
      websocket = new WebSocket(buildAgentV2WebSocketUrl(intent.conversationId))
    } catch {
      failBeforeAdmission(entry, listenerApi)
      return
    }

    if (!isCurrentTransport(entry)) {
      safelyCloseSocket(websocket)
      return
    }

    entry.websocket = websocket
    attachSocketListeners({ entry, idToken, intent, listenerApi })
    await entry.effectSettled
  },
})

startListener({
  actionCreator: AgentV2TransportActions.stopTurnRequested,
  effect: (action, listenerApi) => {
    const entry = activeTransports.get(action.payload.conversationId)
    if (!entry || !isCurrentTransport(entry) || entry.stopRequested) return

    if (!entry.turnStartSent) {
      listenerApi.dispatch(AgentV2TranscriptActions.runStoppedBeforeStart({ conversationId: entry.conversationId }))
      listenerApi.dispatch(AgentV2ConversationsActions.runDidNotStart({ conversationId: entry.conversationId }))
      settleTransport(entry, listenerApi, { closeSocket: true, settleRedux: true })
      return
    }

    entry.stopRequested = true
    listenerApi.dispatch(AgentV2ConversationsActions.runStopRequested({ conversationId: entry.conversationId }))

    const websocket = entry.websocket
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      detachTransport(entry, listenerApi, true)
      return
    }

    try {
      websocket.send(JSON.stringify(buildAgentV2RunStopMessage()))
      listenerApi.dispatch(AgentV2TransportActions.stopSent({ conversationId: entry.conversationId, clientRequestId: entry.clientRequestId }))
    } catch {
      // The command may or may not have reached the server. Durable recovery owns the
      // eventual completed/cancelled/failed outcome and retains the local Stop intent.
      detachTransport(entry, listenerApi, true)
    }
  },
})

startListener({
  actionCreator: AgentV2TransportActions.closeConversationTransport,
  effect: (action) => {
    closeTransportForConversation(action.payload.conversationId)
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.conversationDeletionStarted,
  effect: (action) => {
    closeTransportForConversation(action.payload.conversationId)
  },
})

startListener({
  actionCreator: AgentV2ConversationsActions.deleteConversation,
  effect: (action) => {
    closeTransportForConversation(action.payload.conversationId)
  },
})

startListener({
  matcher: isAnyOf(AgentV2TransportActions.closeAllTransports, AgentV2ConversationsActions.nullifyData),
  effect: () => {
    const entries = [...activeTransports.values()]
    activeTransports.clear()
    for (const entry of entries) retireTransportResources(entry, true)
  },
})

function canStartTurn(intent: AgentV2StartTurnIntent, state: RootState): boolean {
  if (!isValidUuid(intent.clientRequestId) || !isValidUuid(intent.conversationId) || !isValidUuid(intent.matterId) || !intent.inputText.trim()) {
    return false
  }
  if (state.agentV2ConversationsState.deletionStatusByConversationId[intent.conversationId]) return false
  return selectAgentV2RunPhase(state, { conversationId: intent.conversationId }) === AgentV2RunPhase.IDLE
}

function createActiveTransport(intent: AgentV2StartTurnIntent): ActiveAgentV2Transport {
  let resolveEffect = () => {}
  const effectSettled = new Promise<void>((resolve) => {
    resolveEffect = resolve
  })

  return {
    admittedTurnId: null,
    clientRequestId: intent.clientRequestId,
    conversationId: intent.conversationId,
    effectSettled,
    lastAssistantDeltaSequence: null,
    matterArtifactStatusById: new Map(),
    removeSocketListeners: null,
    resolveEffect,
    seenEventIds: new Set(),
    stopRequested: false,
    terminalEventReceived: false,
    turnStartSent: false,
    websocket: null,
  }
}

function attachSocketListeners({
  entry,
  idToken,
  intent,
  listenerApi,
}: {
  entry: ActiveAgentV2Transport
  idToken: string
  intent: AgentV2StartTurnIntent
  listenerApi: TransportListenerApi
}) {
  const websocket = entry.websocket
  if (!websocket) return

  const handleOpen = () => {
    if (!isCurrentSocket(entry, websocket)) return

    try {
      websocket.send(idToken)
      // Once sending turn.start is attempted, admission is uncertain if transport
      // fails. Set this fence before send so we recover instead of enabling submit.
      entry.turnStartSent = true
      websocket.send(
        JSON.stringify(
          buildAgentV2TurnStartMessage({
            clientRequestId: intent.clientRequestId,
            inputText: intent.inputText,
            matterId: intent.matterId,
            matterArtifactSelection: intent.matterArtifactSelection,
            uiState: intent.uiState,
            searchDefaults: intent.searchDefaults,
            selectedSkillIds: intent.selectedSkillIds,
            selectedPaxtonSkillNames: intent.selectedPaxtonSkillNames,
            clientSurface: listenerApi.getState().agentV2ConversationsState.hostSurface,
          })
        )
      )
      listenerApi.dispatch(AgentV2TransportActions.socketOpened({ conversationId: entry.conversationId, clientRequestId: entry.clientRequestId }))
    } catch {
      if (entry.turnStartSent) detachTransport(entry, listenerApi, true)
      else failBeforeAdmission(entry, listenerApi)
    }
  }

  const handleMessage = (messageEvent: MessageEvent) => {
    if (!isCurrentSocket(entry, websocket)) return

    const parseResult = parseAgentV2SocketEvent(messageEvent)
    if (parseResult.outcome === AgentV2SocketParseOutcome.DROP) return
    if (parseResult.outcome === AgentV2SocketParseOutcome.FATAL) {
      detachTransport(entry, listenerApi, true)
      return
    }

    const event = parseResult.event
    if (event.conversation_id !== entry.conversationId) return
    if (event.type === AgentV2ServerEventType.RUN_STARTED && event.payload.client_request_id !== entry.clientRequestId) {
      detachTransport(entry, listenerApi, true)
      return
    }
    if (event.type === AgentV2ServerEventType.ASSISTANT_MESSAGE_DELTA) {
      if (entry.lastAssistantDeltaSequence !== null && event.sequence <= entry.lastAssistantDeltaSequence) return
      entry.lastAssistantDeltaSequence = event.sequence
    } else {
      if (entry.seenEventIds.has(event.event_id)) return
      entry.seenEventIds.add(event.event_id)
    }

    if (isTerminalEvent(event)) {
      entry.terminalEventReceived = true
      listenerApi.dispatch(AgentV2TranscriptActions.serverEventReceived({ conversationId: entry.conversationId, event }))
      fanOutLiveEvent(entry, event, listenerApi)
      handleTerminalEvent(entry, event, listenerApi)
      return
    }

    listenerApi.dispatch(AgentV2TranscriptActions.serverEventReceived({ conversationId: entry.conversationId, event }))
    fanOutLiveEvent(entry, event, listenerApi)
  }

  const handleError = () => {
    if (!isCurrentSocket(entry, websocket) || entry.terminalEventReceived) return
    if (entry.turnStartSent) detachTransport(entry, listenerApi, true)
    else failBeforeAdmission(entry, listenerApi)
  }

  const handleClose = () => {
    if (!isCurrentSocket(entry, websocket)) return
    if (entry.terminalEventReceived) {
      settleTransport(entry, listenerApi, { closeSocket: false, settleRedux: true })
      return
    }
    if (entry.turnStartSent) detachTransport(entry, listenerApi, false)
    else failBeforeAdmission(entry, listenerApi, false)
  }

  websocket.addEventListener('open', handleOpen)
  websocket.addEventListener('message', handleMessage)
  websocket.addEventListener('error', handleError)
  websocket.addEventListener('close', handleClose)
  entry.removeSocketListeners = () => {
    websocket.removeEventListener('open', handleOpen)
    websocket.removeEventListener('message', handleMessage)
    websocket.removeEventListener('error', handleError)
    websocket.removeEventListener('close', handleClose)
  }
}

function fanOutLiveEvent(entry: ActiveAgentV2Transport, event: AgentV2ServerEventEnvelope, listenerApi: TransportListenerApi) {
  if (event.type === AgentV2ServerEventType.RUN_STARTED) {
    if (entry.admittedTurnId && entry.admittedTurnId !== event.turn_id) {
      detachTransport(entry, listenerApi, true)
      return
    }

    const firstAdmissionEvent = entry.admittedTurnId === null
    entry.admittedTurnId = event.turn_id
    if (firstAdmissionEvent) {
      listenerApi.dispatch(
        AgentV2ConversationsActions.runStarted({
          clientRequestId: event.payload.client_request_id,
          conversationId: entry.conversationId,
          turnId: event.turn_id,
        })
      )
      const conversation = selectAgentV2ConversationFromId(listenerApi.getState(), { conversationId: entry.conversationId })
      if (conversation?.title === kNewConversationTitle) {
        listenerApi.dispatch(AgentV2ConversationsActions.generateConversationTitle({ conversationId: entry.conversationId }))
      }
    }
  }

  if (event.type === AgentV2ServerEventType.ASSISTANT_MESSAGE_REFERENCES) {
    const { accepted, dropped } = sanitizeAgentV2References(entry.conversationId, event.payload.references)
    reportDroppedAgentV2References(entry.conversationId, dropped)
    listenerApi.dispatch(AgentV2ReferencesActions.upsertReferences({ conversationId: entry.conversationId, references: accepted }))
  }

  if (event.type === AgentV2ServerEventType.USER_SKILL_SAVED) {
    listenerApi.dispatch(
      UserSkillChatActions.skillSaved({
        conversationId: entry.conversationId,
        eventId: event.event_id,
        turnId: event.turn_id,
        skill: event.payload.skill,
      })
    )
  }

  if (event.type === AgentV2ServerEventType.MATTER_ARTIFACT_EDIT_PROPOSED) {
    const attachedSelection = listenerApi.getState().matterArtifactSelectionState.attachmentsByConversationId[entry.conversationId]
    const editorRange =
      attachedSelection?.selection.matter_artifact_id === event.payload.matter_artifact_id &&
      attachedSelection.selection.matter_artifact_version_id === event.payload.source_matter_artifact_version_id
        ? attachedSelection.editorRange
        : undefined
    const sourceVersion = listenerApi.getState().matterArtifactsState.versionsById[event.payload.source_matter_artifact_version_id]
    listenerApi.dispatch(
      AgentV2MatterArtifactEditProposalActions.proposalReceived({
        conversationId: entry.conversationId,
        originatingTurnId: event.turn_id,
        proposal: event.payload,
        editorRange,
        sourceCreatedAt: sourceVersion?.created_at ?? null,
      })
    )
  }

  if (isDraftEvent(event)) ingestLiveMatterArtifactVersion(entry, event.payload.artifact_version, listenerApi)
}

function handleTerminalEvent(
  entry: ActiveAgentV2Transport,
  event: Extract<
    AgentV2ServerEventEnvelope,
    {
      type: AgentV2ServerEventType.RUN_COMPLETED | AgentV2ServerEventType.RUN_FAILED | AgentV2ServerEventType.RUN_CANCELLED
    }
  >,
  listenerApi: TransportListenerApi
) {
  listenerApi.dispatch(AgentV2ConversationsActions.runTerminalObserved({ conversationId: entry.conversationId, turnId: event.turn_id }))
  listenerApi.dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId: entry.conversationId, forceRefresh: true }))

  if (event.type === AgentV2ServerEventType.RUN_COMPLETED) {
    listenerApi.dispatch(
      AgentV2ConversationsActions.generateConversationFollowUpQuestions({
        conversationId: entry.conversationId,
        assistantMessageId: `${event.turn_id}:assistant`,
      })
    )
  }

  // Terminal is the final protocol event. Retire client ownership and let the server
  // perform its normal post-drain close; do not manufacture a client disconnect.
  settleTransport(entry, listenerApi, { closeSocket: false, settleRedux: true })
}

function ingestLiveMatterArtifactVersion(entry: ActiveAgentV2Transport, version: MatterArtifactVersionRead, listenerApi: TransportListenerApi) {
  if (entry.matterArtifactStatusById.get(version.id) === version.status) return
  entry.matterArtifactStatusById.set(version.id, version.status)

  listenerApi.dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(version))
  if (version.status === MatterArtifactVersionStatus.GENERATING) {
    listenerApi.dispatch(MatterArtifactsActions.startMatterArtifactVersionStatusPoll({ matterArtifactVersionId: version.id }))
  }
}

function failBeforeAdmission(entry: ActiveAgentV2Transport, listenerApi: TransportListenerApi, closeSocket = true) {
  if (!isCurrentTransport(entry)) return
  listenerApi.dispatch(AgentV2TranscriptActions.turnFailedBeforeAdmission({ conversationId: entry.conversationId, message: kConnectionErrorMessage }))
  listenerApi.dispatch(AgentV2ConversationsActions.runDidNotStart({ conversationId: entry.conversationId }))
  settleTransport(entry, listenerApi, { closeSocket, settleRedux: true })
}

function detachTransport(entry: ActiveAgentV2Transport, listenerApi: TransportListenerApi, closeSocket: boolean) {
  if (!isCurrentTransport(entry)) return
  listenerApi.dispatch(AgentV2TransportActions.socketDetached({ conversationId: entry.conversationId, clientRequestId: entry.clientRequestId }))
  listenerApi.dispatch(AgentV2TranscriptActions.transportDetached({ conversationId: entry.conversationId }))
  listenerApi.dispatch(AgentV2ConversationsActions.beginRunRecovery({ conversationId: entry.conversationId }))
  settleTransport(entry, listenerApi, { closeSocket, settleRedux: false })
}

function settleTransport(
  entry: ActiveAgentV2Transport,
  listenerApi: TransportListenerApi,
  { closeSocket, settleRedux }: { closeSocket: boolean; settleRedux: boolean }
) {
  if (!isCurrentTransport(entry)) return
  activeTransports.delete(entry.conversationId)
  retireTransportResources(entry, closeSocket)
  if (settleRedux) {
    listenerApi.dispatch(AgentV2TransportActions.transportSettled({ conversationId: entry.conversationId, clientRequestId: entry.clientRequestId }))
  }
}

function closeTransportForConversation(conversationId: string) {
  const entry = activeTransports.get(conversationId)
  if (!entry) return
  activeTransports.delete(conversationId)
  retireTransportResources(entry, true)
}

function retireTransportResources(entry: ActiveAgentV2Transport, closeSocket: boolean) {
  entry.removeSocketListeners?.()
  entry.removeSocketListeners = null
  if (closeSocket && entry.websocket) safelyCloseSocket(entry.websocket)
  entry.resolveEffect()
}

function safelyCloseSocket(websocket: WebSocket) {
  if (websocket.readyState === WebSocket.CLOSED) return
  try {
    websocket.close()
  } catch {
    // Cleanup is best effort; identity fencing already prevents stale callbacks.
  }
}

function isCurrentTransport(entry: ActiveAgentV2Transport): boolean {
  return activeTransports.get(entry.conversationId) === entry
}

function isCurrentSocket(entry: ActiveAgentV2Transport, websocket: WebSocket): boolean {
  return isCurrentTransport(entry) && entry.websocket === websocket
}

function isTerminalEvent(event: AgentV2ServerEventEnvelope): event is Extract<
  AgentV2ServerEventEnvelope,
  {
    type: AgentV2ServerEventType.RUN_COMPLETED | AgentV2ServerEventType.RUN_FAILED | AgentV2ServerEventType.RUN_CANCELLED
  }
> {
  return (
    event.type === AgentV2ServerEventType.RUN_COMPLETED ||
    event.type === AgentV2ServerEventType.RUN_FAILED ||
    event.type === AgentV2ServerEventType.RUN_CANCELLED
  )
}

function isDraftEvent(event: AgentV2ServerEventEnvelope): event is Extract<
  AgentV2ServerEventEnvelope,
  {
    type:
      | AgentV2ServerEventType.DRAFT_STARTED
      | AgentV2ServerEventType.DRAFT_COMPLETED
      | AgentV2ServerEventType.DRAFT_FAILED
      | AgentV2ServerEventType.DRAFT_CANCELLED
  }
> {
  return (
    event.type === AgentV2ServerEventType.DRAFT_STARTED ||
    event.type === AgentV2ServerEventType.DRAFT_COMPLETED ||
    event.type === AgentV2ServerEventType.DRAFT_FAILED ||
    event.type === AgentV2ServerEventType.DRAFT_CANCELLED
  )
}
